import { darken, SxProps, Theme } from '@mui/material'

import {
  CHART_CARD_BACKGROUND,
  CHART_TITLE_FONT_SIZE,
  CHART_TITLE_LINE_HEIGHT,
  HEADER_BUTTON_RADIUS_PX,
  HEADER_CHIP_MAX_WIDTH_PX,
  INPUT_BORDER,
  NAME_HEADING_MAX_WIDTH_PX,
  NEUTRAL_GREY_500,
} from '@/common/constants'

/**
 * The title of a chart card, per Figma: 20px bold in the neutral grey, on a flat
 * line height so it shares a baseline with the dropdowns standing next to it.
 * MUI's own `h5` is 24px/400 with a 1.334 line height, which both outweighs the
 * controls and pushes the header row taller than they are.
 *
 * Applied through `ChartCardHeader` for the cards that have one; exported for the
 * few titles that are not part of a header row (error/suspense fallbacks, the map
 * cards' own header component).
 */
export const CHART_CARD_TITLE_SX = {
  color: NEUTRAL_GREY_500,
  fontSize: CHART_TITLE_FONT_SIZE,
  fontWeight: 700,
  lineHeight: CHART_TITLE_LINE_HEIGHT,
} satisfies SxProps<Theme>

/**
 * The icon buttons in the top bar. They sit on the bar's grey fill, so they need
 * an explicit white background of their own to read as controls — a transparent
 * button with only a border disappears into the grey.
 *
 * Shared by `HeaderBar` (filter, public page) and `ButtonMenu` (create, download,
 * IPS apps) so buttons standing side by side cannot drift apart.
 */
export const SQUARE_ICON_BUTTON_SX = {
  backgroundColor: 'common.white',
  border: `1px solid ${INPUT_BORDER}`,
  borderRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  // MUI's default action hover is a translucent black that darkens toward the bar
  // behind it; the chart-card grey is the flat tint the design uses instead.
  '&:hover': { backgroundColor: CHART_CARD_BACKGROUND },
} satisfies SxProps<Theme>

/**
 * The main action of a page or form in the new theme (Edit, Save, Submit). White on
 * `primary.main` is 3.2:1, below WCAG AA. On `primary.dark` it is 6:1. Same fill as the Confirm
 * button of `ThemedCreateModal`.
 */
export const PRIMARY_BUTTON_SX = {
  bgcolor: 'primary.dark',
  borderRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: (theme: Theme) => darken(theme.palette.primary.dark, 0.2),
    boxShadow: 'none',
  },
} satisfies SxProps<Theme>

/**
 * The other actions of a page or form in the new theme (Publish, Add Time Frame). The same white
 * fill, border and radius as the icon buttons of the page header (`SQUARE_ICON_BUTTON_SX`).
 */
export const SECONDARY_BUTTON_SX = {
  bgcolor: 'common.white',
  border: `1px solid ${INPUT_BORDER}`,
  borderRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  color: 'primary.dark',
  '&:hover': { bgcolor: CHART_CARD_BACKGROUND, borderColor: INPUT_BORDER },
} satisfies SxProps<Theme>

/** A text button in the new theme (Cancel, a back link). `primary.main` text on white is 3.2:1. */
export const TEXT_BUTTON_SX = {
  color: 'primary.dark',
} satisfies SxProps<Theme>

/**
 * The applied-filter chips in the top bar. Like the icon buttons they sit on the
 * bar's grey fill, and MUI's default chip fill (`action.selected`, an 8%-alpha
 * black) composites to within a hair of that grey — so the chips need the same
 * explicit white fill and neutral outline to read as controls at all.
 *
 * Tokens are shared with `SQUARE_ICON_BUTTON_SX` so a chip and the button beside
 * it cannot drift apart in weight, border, or radius.
 */
export const FILTER_CHIP_SX = {
  backgroundColor: 'common.white',
  border: `1px solid ${INPUT_BORDER}`,
  borderRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  color: 'text.primary',
  // A chip's text is user data and can be far wider than the bar. MUI already gives
  // `.MuiChip-label` `overflow: hidden; text-overflow: ellipsis; white-space: nowrap`,
  // so a ceiling is all that is needed to get a native ellipsis. The string itself is
  // untouched: the DOM text and the chip's accessible name still carry the full value,
  // and `HeaderBar` puts it behind a tooltip.
  maxWidth: `${HEADER_CHIP_MAX_WIDTH_PX}px`,
  // A flex item defaults to `min-width: auto`, which is its content width - that is why
  // one long chip pushes the bar's buttons off the end instead of yielding. Zeroing it
  // here (and on the rows in `HeaderBar`) lets the chip shrink, with `maxWidth` as the
  // ceiling rather than the fixed width.
  minWidth: 0,
  // MUI's default delete icon is a 26%-alpha black, invisible on the new white fill.
  '& .MuiChip-deleteIcon': {
    color: 'text.secondary',
    '&:hover': { color: 'text.primary' },
  },
} satisfies SxProps<Theme>

/**
 * One cell of a segmented control, in the three states it can be in. Shared by both
 * groups below so a `[Enforced|All] Hours` segment and a weekday segment cannot drift
 * apart — MUI models them with two unrelated component families.
 *
 * The unselected cell borrows `SQUARE_ICON_BUTTON_SX`'s white fill, `INPUT_BORDER`
 * outline, and radius; the selected one states itself in `primary.dark`. MUI's own
 * defaults are a translucent `divider` outline with an `action.selected` grey for the
 * chosen side, which reads as barely-chosen next to the bar's white bordered buttons.
 *
 * The selected fill is `primary.dark`, not `primary.main`: the segment carries white
 * text, and white on `primary.main` is only 3.2:1, below the WCAG AA 4.5:1 for normal
 * text (axe `color-contrast`). White on `primary.dark` is 5.97:1. The header's filter
 * button can stay on `primary.main` because it is an icon, which needs only 3:1.
 */
const SEGMENT_SX = {
  backgroundColor: 'common.white',
  border: `1px solid ${INPUT_BORDER}`,
  color: 'text.secondary',
  '&:hover': { backgroundColor: CHART_CARD_BACKGROUND },
}

const SEGMENT_SELECTED_SX = {
  backgroundColor: 'primary.dark',
  borderColor: 'primary.dark',
  color: 'common.white',
  // `Button variant="contained"` is elevated by default; a segment is flush with its
  // neighbours, so the shadow only muddies the seam.
  boxShadow: 'none',
  // The rest fill is already `primary.dark`, so the hover goes one step darker to stay
  // visible (the same as the map Metrics button).
  '&:hover': {
    backgroundColor: (theme: Theme) => darken(theme.palette.primary.dark, 0.2),
    borderColor: (theme: Theme) => darken(theme.palette.primary.dark, 0.2),
    boxShadow: 'none',
  },
}

// A mode can be disabled rather than hidden (see `HoursToggle`), so it still has to
// look like a segment of the control, just an unusable one. A segment can be both
// chosen and disabled — HoursToggle keeps the URL's mode selected even where the
// region has no data for it — so the selected rules below are guarded with
// `:not(.Mui-disabled)`. Without that the segment kept the selected border and
// hover fill while wearing the disabled body.
const SEGMENT_DISABLED_SX = {
  backgroundColor: CHART_CARD_BACKGROUND,
  color: 'text.disabled',
}

const SEGMENT_RADIUS_SX = {
  borderTopLeftRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  borderBottomLeftRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
}

const SEGMENT_RADIUS_END_SX = {
  borderTopRightRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  borderBottomRightRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
}

// Restated because the segment's border shorthand outranks MUI's own seam rule:
// without it neighbouring cells meet as a doubled 2px line.
const SEGMENT_SEAM_SX = {
  marginLeft: '-1px',
  borderLeft: '1px solid transparent',
}

/**
 * The `ToggleButtonGroup` segmented controls — `[Enforced|All] Hours` and `[Map|Table]`,
 * which mark the chosen cell with the `Mui-selected` class.
 */
export const TOGGLE_GROUP_SX = {
  borderRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  '& .MuiToggleButton-root': {
    ...SEGMENT_SX,
    '&.Mui-selected:not(.Mui-disabled)': SEGMENT_SELECTED_SX,
    '&.Mui-disabled': SEGMENT_DISABLED_SX,
  },
  '& .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton': SEGMENT_SEAM_SX,
  '& .MuiToggleButtonGroup-firstButton': SEGMENT_RADIUS_SX,
  '& .MuiToggleButtonGroup-lastButton': SEGMENT_RADIUS_END_SX,
} satisfies SxProps<Theme>

/**
 * The `ButtonGroup` segmented controls — the weekday and day-type toggles in the filter
 * panel. These are multi-select rather than exclusive, and mark the chosen cells by
 * swapping each `Button` to `variant="contained"` instead of by a selected class.
 */
export const BUTTON_GROUP_TOGGLE_SX = {
  borderRadius: `${HEADER_BUTTON_RADIUS_PX}px`,
  '& .MuiButton-root': {
    ...SEGMENT_SX,
    '&.Mui-disabled': SEGMENT_DISABLED_SX,
  },
  '& .MuiButton-contained:not(.Mui-disabled)': SEGMENT_SELECTED_SX,
  '& .MuiButtonGroup-middleButton, & .MuiButtonGroup-lastButton': SEGMENT_SEAM_SX,
  '& .MuiButtonGroup-firstButton': SEGMENT_RADIUS_SX,
  '& .MuiButtonGroup-lastButton': SEGMENT_RADIUS_END_SX,
} satisfies SxProps<Theme>

/**
 * The shape-name heading of a map hover popup. Shape names are user data: one
 * Bloomington shape is a comma-separated list of sub-shape ids inside a *single*
 * name, past 100 characters.
 *
 * `word-break: break-word` - what these headings carried before - only wraps, it does
 * not bound, so the name filled `.mapboxgl-popup`'s `min(600px, 90vw)` cap and wrapped
 * into a tall block. On the `.regulation-hover-popup` variants it was worse: that class
 * sets `max-width: unset !important`, so there was no bound at all.
 *
 * Height is bounded as well as width. Wrapping alone was not enough: at this width the
 * Bloomington name runs to thirteen lines, which overflowed the map container and pushed
 * the popup's actual metrics out of view - the popup stopped showing the data it exists
 * for. Two lines keeps it a predictable size.
 *
 * Same clamp-plus-`title` bargain as `MetricTileTitle`. The `title` is a best effort here:
 * a hover popup is dismissed by the map canvas's own `mouseleave`, so moving the pointer
 * onto it may close it first. The full name stays reachable for certain in the header
 * chip's tooltip, the shape picker and the Table view.
 *
 * `overflow-wrap: anywhere` rather than `break-word` because only `anywhere` lowers the
 * element's min-content width, which is what lets a name with no break opportunity honour
 * the cap.
 *
 * Exported separately from `MapPopupTitle` because one call site (the Regulations map)
 * heads its name with the legacy `Text` component, not `Typography`.
 */
export const MAP_POPUP_TITLE_SX = {
  maxWidth: `${NAME_HEADING_MAX_WIDTH_PX}px`,
  overflowWrap: 'anywhere',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
} satisfies SxProps<Theme>
